import React, { useState } from 'react';
import './ProductForm.css';
import { AiFillHome } from 'react-icons/ai';
import { MdRestaurantMenu } from 'react-icons/md';
import { FaMoneyBillWave, FaUserFriends, FaGift } from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';

const navItems = [
  'Home',
  'Product',
  'Sales',
  'Members',
  'Promotion',
  'Restaurant',
];

const navIcons = {
  Home: <AiFillHome className="nav-icon" />,
  Product: <MdRestaurantMenu className="nav-icon" />,
  Sales: <FaMoneyBillWave className="nav-icon" />,
  Members: <FaUserFriends className="nav-icon" />,
  Promotion: <FaGift className="nav-icon" />,
  Restaurant: <GiKnifeFork className="nav-icon" />,
};

const initialCategories = [
  'noodle',
  'rice', 
  'chicken',
  'seafood',
  'drink'
];

const statusOptions = [
  'published',
  'draft',
  'archived',
  'scheduled'
];

const promoOptions = ['WELCOME GIFT', 'B1F1', 'WWGIFT', 'NONE'];
const visibilityOptions = ['visible', 'hidden'];
const seasonalOptions = ['regular', 'seasonal'];

function ProductForm({ onNavigate }) {
  const [currentPage, setCurrentPage] = useState('Product');
  const [categories, setCategories] = useState(initialCategories);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    productName: '',
    productDescription: '',
    basePrice: '',
    membershipPrice: '',
    membershipEnabled: true,
    promoCode: 'NONE',
    visibility: 'visible',
    seasonal: 'regular',
    quantity: '',
    category: 'noodle',
    imageUrl: '' // 改为图片URL字段
  });
  const [descriptionFormat, setDescriptionFormat] = useState({
    bold: false,
    italic: false,
    underline: false
  });

  const handleNavClick = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDescriptionFormat = (format) => {
    setDescriptionFormat(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
  };

  const handleFileUpload = (event) => {
    // 改为处理图片URL输入
    const imageUrl = event.target.value;
    setFormData(prev => ({
      ...prev,
      imageUrl: imageUrl
    }));
  };

  const handleCreateCategory = () => {
    setShowNewCategoryInput(true);
  };

  const handleAddCategory = () => {
    if (newCategory.trim()) {
      const categoryName = newCategory.trim().toLowerCase();
      if (!categories.includes(categoryName)) {
        setCategories(prev => [...prev, categoryName]);
        setFormData(prev => ({ ...prev, category: categoryName }));
      }
      setNewCategory('');
      setShowNewCategoryInput(false);
    }
  };

  const handleCancelCategory = () => {
    setNewCategory('');
    setShowNewCategoryInput(false);
  };

  // 工具函数：文件转base64（临时移除压缩功能）
  async function fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  const handleSave = async () => {
    if (!formData.productName.trim()) {
      alert('Please enter product name');
      return;
    }

    if (!formData.basePrice || parseFloat(formData.basePrice) <= 0) {
      alert('Please enter a valid price');
      return;
    }

    setIsSubmitting(true);

    try {
      // 生成sku（如无输入则用时间戳）
      const sku = formData.sku || `sku${Date.now()}`;
      // 处理图片URL
      let imageUrl = formData.imageUrl || '';
      // categoryId映射（可根据实际调整）
      const categoryIdMap = {
        'rice': 13,
        'noodle': 14,
        'drinks': 15,
        'fast-order': 16,
        'chicken': 17,
        'seafood': 18,
        'drink': 15
      };
      const category_id = categoryIdMap[formData.category] || 13;
      // 构造product对象（包含imageUrl字段）
      const product = {
        sku,
        name: formData.productName.trim(),
        price: parseFloat(formData.basePrice),
        status: 1, // 1=Enabled
        visibility: 4, // 4=Catalog, Search
        type_id: 'simple',
        attribute_set_id: 4, // 可根据实际调整
        imageUrl: imageUrl,
        custom_attributes: [
          { attribute_code: 'description', value: formData.productDescription.trim() }
        ],
        extension_attributes: {
          category_links: [
            { category_id }
          ]
        }
      };
      // 构造请求体，只包含product字段
      const requestBody = { product };
      // 发送POST请求到服务器
      const response = await fetch('http://localhost:8080/catalog/magento/additem', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      if (response.ok) {
        const responseText = await response.text();
        let newProduct;
        try {
          newProduct = JSON.parse(responseText);
        } catch (e) {
          console.error('Response is not valid JSON:', responseText);
          throw new Error('Server returned invalid JSON response');
        }
        console.log('Product added:', newProduct);
        alert('Product added successfully!');
        // 重置表单
        setFormData({
          productName: '',
          productDescription: '',
          basePrice: '',
          quantity: '',
          seasonal: 'no',
          status: 'published',
          category: 'noodle',
          imageUrl: ''
        });
        // 返回产品页面
        if (onNavigate) {
          onNavigate('Product');
        }
      } else {
        const responseText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          console.error('Error response is not valid JSON:', responseText);
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        throw new Error(errorData.error || 'Add product failed');
      }
    } catch (error) {
      console.error('Error adding product:', error);
      alert(`Add product failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      onNavigate('Product');
    }
  };

  const getFormattedDescription = () => {
    let text = formData.productDescription;
    if (descriptionFormat.bold) text = `**${text}**`;
    if (descriptionFormat.italic) text = `*${text}*`;
    if (descriptionFormat.underline) text = `__${text}__`;
    return text;
  };

  const applyFormatting = (text, format) => {
    const textarea = document.getElementById('productDescription');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = text.substring(start, end);
    
    let formattedText = '';
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'underline':
        formattedText = `__${selectedText}__`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newText = text.substring(0, start) + formattedText + text.substring(end);
    handleInputChange('productDescription', newText);
    
    // 重新设置光标位置
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  };

  return (
    <div className="productform-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li 
              key={item} 
              className={item === 'Product' ? 'active' : ''}
              onClick={() => handleNavClick(item)}
            >
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>
      
      <main className="productform-main">
        <div className="form-header">
          <h1>Add New Product</h1>
        </div>

        <div className="form-content">
          {/* 左侧卡片区域 */}
          <div className="left-panel">
            {/* Thumbnail卡片 */}
            <div className="form-card">
              <h3>Thumbnail</h3>
              <div className="thumbnail-upload">
                <div className="upload-area">
                  <input
                    type="text"
                    placeholder="请输入图片的完整公网URL，例如：https://your-magento.com/media/catalog/product/x/x/xxx.jpg"
                    value={formData.imageUrl}
                    onChange={handleFileUpload}
                    className="image-url-input"
                  />
                </div>
                {formData.imageUrl && (
                  <div className="image-preview">
                    <img src={formData.imageUrl} alt="预览" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  </div>
                )}
              </div>
            </div>

            {/* Status卡片 */}
            <div className="form-card">
              <h3>Status</h3>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="status-select"
              >
                {statusOptions.map(option => (
                  <option key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Categories卡片 */}
            <div className="form-card">
              <h3>Categories</h3>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="category-select"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
              {!showNewCategoryInput ? (
                <button 
                  className="create-category-btn"
                  onClick={handleCreateCategory}
                >
                + Create New Category
              </button>
              ) : (
                <div className="new-category-input">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                    className="category-input"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddCategory();
                      }
                    }}
                  />
                  <div className="category-actions">
                    <button 
                      className="add-category-btn"
                      onClick={handleAddCategory}
                    >
                      Add
                    </button>
                    <button 
                      className="cancel-category-btn"
                      onClick={handleCancelCategory}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            {/* Visibility卡片 */}
            <div className="form-card">
              <h3>Visibility</h3>
              <select
                value={formData.visibility}
                onChange={e => handleInputChange('visibility', e.target.value)}
                className="category-select"
              >
                {visibilityOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            {/* Seasonal卡片 */}
            <div className="form-card">
              <h3>Seasonal</h3>
              <select
                value={formData.seasonal}
                onChange={e => handleInputChange('seasonal', e.target.value)}
                className="category-select"
              >
                {seasonalOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 右侧表单区域 */}
          <div className="right-panel">
            <div className="form-section">
              <h2>General</h2>
              
              <div className="form-group">
                <label htmlFor="productName">
                  Product Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="productName"
                  value={formData.productName}
                  onChange={(e) => handleInputChange('productName', e.target.value)}
                  placeholder="Enter product name"
                  className="form-input"
                />
                <p className="form-hint">A product name is required and recommended to be unique</p>
              </div>

              <div className="form-group">
                <label htmlFor="productDescription">Product Description</label>
                <div className="description-toolbar">
                  <button
                    type="button"
                    className={`format-btn ${descriptionFormat.bold ? 'active' : ''}`}
                    onClick={() => applyFormatting(formData.productDescription, 'bold')}
                    title="Bold (Ctrl+B)"
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    className={`format-btn ${descriptionFormat.italic ? 'active' : ''}`}
                    onClick={() => applyFormatting(formData.productDescription, 'italic')}
                    title="Italic (Ctrl+I)"
                  >
                    <em>I</em>
                  </button>
                  <button
                    type="button"
                    className={`format-btn ${descriptionFormat.underline ? 'active' : ''}`}
                    onClick={() => applyFormatting(formData.productDescription, 'underline')}
                    title="Underline (Ctrl+U)"
                  >
                    <u>U</u>
                  </button>
                </div>
                <textarea
                  id="productDescription"
                  value={formData.productDescription}
                  onChange={(e) => handleInputChange('productDescription', e.target.value)}
                  placeholder="Enter product description"
                  className="form-textarea"
                  rows="6"
                />
                <p className="form-hint">Provide a detailed description of your product</p>
              </div>

              <div className="form-group">
                <label htmlFor="media">Media</label>
                <div className="media-upload">
                  <input
                    type="text"
                    placeholder="请输入图片的完整公网URL，例如：https://your-magento.com/media/catalog/product/x/x/xxx.jpg"
                    value={formData.imageUrl}
                    onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                    className="form-input"
                  />
                </div>
                {formData.imageUrl && (
                  <div className="media-gallery">
                    <div className="media-item">
                      <img src={formData.imageUrl} alt="预览" style={{ maxWidth: '100px', maxHeight: '100px', marginRight: '10px' }} />
                      <span>图片URL已设置</span>
                      <button 
                        type="button" 
                        className="remove-media-btn"
                        onClick={() => handleInputChange('imageUrl', '')}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
                <p className="form-hint">输入图片的完整公网URL</p>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="basePrice">Base Price</label>
                  <input
                    type="number"
                    id="basePrice"
                    value={formData.basePrice}
                    onChange={(e) => handleInputChange('basePrice', e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="quantity">Quantity</label>
                  <input
                    type="number"
                    id="quantity"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    placeholder="0"
                    min="0"
                    className="form-input"
                  />
                </div>
              </div>

              {/* 会员价 */}
              <div className="form-group">
                <label htmlFor="membershipPrice">Membership Price</label>
                <input
                  type="number"
                  id="membershipPrice"
                  value={formData.membershipPrice}
                  onChange={e => handleInputChange('membershipPrice', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="form-input"
                />
                <label style={{marginLeft: '8px'}}>
                  <input
                    type="checkbox"
                    checked={formData.membershipEnabled}
                    onChange={e => handleInputChange('membershipEnabled', e.target.checked)}
                  />
                  Enable Membership Price
                </label>
              </div>
              {/* 促销码 */}
              <div className="form-group">
                <label htmlFor="promoCode">Eligible Promo Code</label>
                <select
                  id="promoCode"
                  value={formData.promoCode}
                  onChange={e => handleInputChange('promoCode', e.target.value)}
                  className="form-select"
                >
                  {promoOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 底部按钮 */}
            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="save-btn"
                onClick={handleSave}
                disabled={!formData.productName.trim() || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default ProductForm; 